import { useState, useEffect } from 'react';
import { Pencil, Trash2, X } from 'lucide-react';
import { Button } from './Button';
import { Modal } from './Modal';
import { useToast } from '../hooks/useToast';
import { useLanguage } from '../hooks/useLanguage';
import { getGroups, createGroup, updateGroup, deleteGroup, getGroupMembers, getLeads, updateLead, markAttendance, getActivities } from '../services/api';

export const GroupsManager = () => {
  const [groups, setGroups] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [membersModalOpen, setMembersModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false);
  const [availableLeads, setAvailableLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    group_name: '',
    description: '',
    activity_id: '',
  });
  const toast = useToast();
  const { t, language } = useLanguage();

  useEffect(() => {
    fetchGroups();
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    try {
      const data = await getActivities();
      setActivities(data);
    } catch (error) {
      console.error('Failed to load activities:', error);
      toast.error(t('failedToLoadActivities') || 'Failed to load activities');
    }
  };

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const data = await getGroups();
      setGroups(data);
    } catch (error) {
      console.error('Failed to load groups:', error);
      toast.error(t('failedToLoadGroups') || 'Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (group = null) => {
    if (group) {
      setEditingGroup(group);
      setFormData({
        group_name: group.group_name,
        description: group.description || '',
        activity_id: group.activity_id || '',
      });
    } else {
      setEditingGroup(null);
      setFormData({
        group_name: '',
        description: '',
        activity_id: '',
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingGroup(null);
    setFormData({
      group_name: '',
      description: '',
      activity_id: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const submitData = {
        ...formData,
        activity_id: formData.activity_id ? parseInt(formData.activity_id) : null,
      };

      console.log('Submitting group data:', submitData);
      console.log('Editing group:', editingGroup);

      if (editingGroup) {
        await updateGroup(editingGroup.id, submitData);
        toast.success(t('groupUpdated') || 'Group updated successfully');
      } else {
        await createGroup(submitData);
        toast.success(t('groupCreated') || 'Group created successfully');
      }

      handleCloseModal();
      fetchGroups();
    } catch (error) {
      console.error('Group save error:', error);
      const errorCode = error.response?.data?.error?.code;
      if (errorCode === 'GROUP_ALREADY_EXISTS') {
        toast.error(t('groupAlreadyExists') || 'A group with this name already exists');
      } else {
        toast.error(t('failedToSaveGroup') || 'Failed to save group');
      }
    }
  };

  const handleDelete = async (group) => {
    if (!confirm(t('deleteGroupConfirm') || `Are you sure you want to delete "${group.group_name}"?`)) {
      return;
    }

    try {
      await deleteGroup(group.id);
      toast.success(t('groupDeleted') || 'Group deleted successfully');
      fetchGroups();
    } catch (error) {
      const errorCode = error.response?.data?.error?.code;
      if (errorCode === 'GROUP_HAS_MEMBERS') {
        toast.error(t('cannotDeleteGroupWithMembers') || 'Cannot delete group with members. Remove all members first.');
      } else {
        toast.error(t('groupDeleteFailed') || 'Failed to delete group');
      }
    }
  };

  const handleToggleActive = async (group) => {
    try {
      await updateGroup(group.id, {
        is_active: !group.is_active
      });
      fetchGroups();
    } catch (error) {
      toast.error(t('failedToUpdateGroup') || 'Failed to update group');
    }
  };

  const handleViewMembers = async (group) => {
    setSelectedGroup(group);
    setMembersModalOpen(true);
    await fetchGroupMembers(group.id);
  };

  const fetchGroupMembers = async (groupId) => {
    try {
      setLoadingMembers(true);
      const members = await getGroupMembers(groupId);
      // Sort members alphabetically by name
      const sortedMembers = members.sort((a, b) => {
        const nameA = (a.field_data?.full_name || '').toLowerCase();
        const nameB = (b.field_data?.full_name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setGroupMembers(sortedMembers);
    } catch (error) {
      console.error('Failed to load group members:', error);
      toast.error(t('failedToLoadMembers') || 'Failed to load group members');
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleCloseMembersModal = () => {
    setMembersModalOpen(false);
    setSelectedGroup(null);
    setGroupMembers([]);
  };

  const handleRemoveMember = async (leadId) => {
    if (!confirm(t('confirmRemoveMember') || 'Are you sure you want to remove this member from the group?')) {
      return;
    }

    try {
      await updateLead(leadId, { group_id: null });
      toast.success(t('memberRemoved') || 'Member removed from group successfully');
      
      // Refresh members list
      if (selectedGroup) {
        await fetchGroupMembers(selectedGroup.id);
      }
      
      // Refresh groups to update member count
      fetchGroups();
    } catch (error) {
      console.error('Failed to remove member:', error);
      toast.error(t('failedToRemoveMember') || 'Failed to remove member from group');
    }
  };

  const handleToggleHealthDeclaration = async (leadId, currentStatus) => {
    try {
      const newStatus = currentStatus ? null : true; // Toggle: true -> null, null/false -> true
      
      // Use markAttendance API which handles health declaration updates
      // Pass null for attendance_status since we're only updating health declaration
      await markAttendance(leadId, null, newStatus);
      
      toast.success(t('healthDeclarationUpdated') || 'Health declaration updated successfully');
      
      // Refresh members list to show updated status
      if (selectedGroup) {
        await fetchGroupMembers(selectedGroup.id);
      }
    } catch (error) {
      console.error('Failed to update health declaration:', error);
      toast.error(t('failedToUpdateHealthDeclaration') || 'Failed to update health declaration');
    }
  };

  const handleOpenAddMemberModal = async () => {
    setAddMemberModalOpen(true);
    setSearchTerm('');
    await fetchAvailableLeads();
  };

  const fetchAvailableLeads = async () => {
    try {
      setLoadingLeads(true);
      // Fetch all leads without a group or with different group
      const leads = await getLeads({ group_id: 'null' });
      setAvailableLeads(leads);
    } catch (error) {
      console.error('Failed to load available leads:', error);
      toast.error(t('failedToLoadLeads') || 'Failed to load available leads');
    } finally {
      setLoadingLeads(false);
    }
  };

  const handleCloseAddMemberModal = () => {
    setAddMemberModalOpen(false);
    setAvailableLeads([]);
    setSearchTerm('');
  };

  const handleAddMember = async (leadId) => {
    if (!selectedGroup) return;

    try {
      await updateLead(leadId, { group_id: selectedGroup.id });
      toast.success(t('memberAdded') || 'Member added to group successfully');
      
      // Refresh members list
      await fetchGroupMembers(selectedGroup.id);
      
      // Refresh available leads
      await fetchAvailableLeads();
      
      // Refresh groups to update member count
      fetchGroups();
    } catch (error) {
      console.error('Failed to add member:', error);
      toast.error(t('failedToAddMember') || 'Failed to add member to group');
    }
  };

  // Filter available leads based on search term
  const filteredLeads = availableLeads.filter(lead => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const fullName = lead.field_data?.full_name?.toLowerCase() || '';
    const phone = lead.field_data?.phone?.toLowerCase() || '';
    const email = lead.field_data?.email?.toLowerCase() || '';
    return fullName.includes(searchLower) || phone.includes(searchLower) || email.includes(searchLower);
  });

  if (loading) {
    return <div className="text-center py-8">{t('loading')}</div>;
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
            {t('groups') || 'Groups'}
          </h2>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            {t('manageGroups') || 'Manage groups and assign them to events'}
          </p>
        </div>
        <Button onClick={() => handleOpenModal()} className="w-full sm:w-auto">
          <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
            <path d="M12 4v16m8-8H4" />
          </svg>
          {t('addGroup') || 'Add Group'}
        </Button>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {groups.length === 0 ? (
          <div className="bg-white shadow-md rounded-lg p-6 text-center text-gray-500">
            {t('noGroups') || 'No groups found'}
          </div>
        ) : (
          groups.map((group) => (
            <div key={group.id} className="bg-white shadow-md rounded-lg p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">{group.group_name}</h3>
                  {group.description && (
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">{group.description}</p>
                  )}
                </div>
                <button
                  onClick={() => handleToggleActive(group)}
                  className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                    group.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {group.is_active ? t('active') || 'Active' : t('inactive') || 'Inactive'}
                </button>
              </div>

              <div className="flex items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                    <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <span>{group.member_count || 0} {t('members') || 'Members'}</span>
                </div>
                <div className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                    <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>{group.event_count || 0} {t('events') || 'Events'}</span>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t">
                <button
                  onClick={() => handleViewMembers(group)}
                  className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                  title={t('viewMembers') || 'View Members'}
                >
                  <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                    <path d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleOpenModal(group)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title={t('edit') || 'Edit'}
                >
                  <Pencil className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleDelete(group)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title={t('delete') || 'Delete'}
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                {t('name') || 'Name'}
              </th>
              <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                {t('description') || 'Description'}
              </th>
              <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                {t('members') || 'Members'}
              </th>
              <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                {t('events') || 'Events'}
              </th>
              <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                {t('status') || 'Status'}
              </th>
              <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                {t('actions') || 'Actions'}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {groups.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                  {t('noGroups') || 'No groups found'}
                </td>
              </tr>
            ) : (
              groups.map((group) => (
                <tr key={group.id}>
                  <td className={`px-6 py-4 whitespace-nowrap ${language === 'he' ? 'text-right' : 'text-left'}`}>
                    <div className="text-sm font-medium text-gray-900">
                      {group.group_name}
                    </div>
                  </td>
                  <td className={`px-6 py-4 ${language === 'he' ? 'text-right' : 'text-left'}`}>
                    <div className="text-sm text-gray-600 max-w-xs truncate">
                      {group.description || '-'}
                    </div>
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap ${language === 'he' ? 'text-right' : 'text-left'}`}>
                    <div className="text-sm text-gray-600">
                      {group.member_count || 0}
                    </div>
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap ${language === 'he' ? 'text-right' : 'text-left'}`}>
                    <div className="text-sm text-gray-600">
                      {group.event_count || 0}
                    </div>
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap ${language === 'he' ? 'text-right' : 'text-left'}`}>
                    <button
                      onClick={() => handleToggleActive(group)}
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        group.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {group.is_active ? t('active') || 'Active' : t('inactive') || 'Inactive'}
                    </button>
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${language === 'he' ? 'text-right' : 'text-left'}`}>
                    <div className={`flex items-center gap-2 ${language === 'he' ? 'justify-end' : 'justify-start'}`}>
                      <button
                        onClick={() => handleViewMembers(group)}
                        className="text-green-600 hover:text-green-900"
                        title={t('viewMembers') || 'View Members'}
                      >
                        <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                          <path d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleOpenModal(group)}
                        className="text-blue-600 hover:text-blue-900"
                        title={t('edit') || 'Edit'}
                      >
                        <Pencil className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(group)}
                        className="text-red-600 hover:text-red-900"
                        title={t('delete') || 'Delete'}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title={editingGroup ? (t('editGroup') || 'Edit Group') : (t('addGroup') || 'Add Group')}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('groupName') || 'Group Name'}
            </label>
            <input
              type="text"
              value={formData.group_name}
              onChange={(e) => setFormData({ ...formData, group_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              placeholder={t('enterGroupName') || 'Enter group name'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('activity') || 'Activity'}
            </label>
            <select
              value={formData.activity_id}
              onChange={(e) => setFormData({ ...formData, activity_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('selectActivity') || 'Select Activity (Optional)'}</option>
              {activities.map((activity) => (
                <option key={activity.id} value={activity.id}>
                  {activity.activity_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('description') || 'Description'}
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="3"
              placeholder={t('enterDescription') || 'Enter description (optional)'}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="submit" variant="primary" className="flex-1">
              {editingGroup ? (t('update') || 'Update') : (t('create') || 'Create')}
            </Button>
            <Button type="button" variant="secondary" onClick={handleCloseModal} className="flex-1">
              {t('cancel') || 'Cancel'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Group Members Modal */}
      <Modal
        isOpen={membersModalOpen}
        onClose={handleCloseMembersModal}
        title={`${t('groupMembers') || 'Group Members'} - ${selectedGroup?.group_name || ''}`}
      >
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              {t('totalMembers') || 'Total Members'}: {groupMembers.length}
            </p>
            <Button onClick={handleOpenAddMemberModal} size="sm">
              <svg className="w-4 h-4 mr-1" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                <path d="M12 4v16m8-8H4" />
              </svg>
              {t('addMember') || 'Add Member'}
            </Button>
          </div>

          {loadingMembers ? (
            <div className="text-center py-8">{t('loading')}</div>
          ) : groupMembers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {t('noMembers') || 'No members in this group'}
            </div>
          ) : (
            <>
              {/* Mobile Card View for Members */}
              <div className="md:hidden max-h-96 overflow-y-auto space-y-3">
                {groupMembers.map((member) => (
                  <div key={member.id} className="bg-gray-50 rounded-lg p-3 space-y-2">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{member.field_data?.full_name || '-'}</p>
                        <p className="text-sm text-gray-600">{member.field_data?.phone || '-'}</p>
                      </div>
                      <button
                        onClick={() => handleRemoveMember(member.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                        title={t('remove') || 'Remove'}
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        member.status_name === 'Active' || member.status_name === 'פעיל'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {member.status_name || '-'}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-600">{t('healthDeclaration') || 'Health'}</span>
                        <button
                          onClick={() => handleToggleHealthDeclaration(member.id, member.health_declaration)}
                          className={`flex items-center justify-center w-8 h-8 rounded-full transition-all ${
                            member.health_declaration
                              ? 'bg-green-100 border-2 border-green-500'
                              : 'bg-gray-100 border-2 border-gray-300'
                          }`}
                        >
                          {member.health_declaration ? (
                            <svg className="w-4 h-4 text-green-600" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                              <path d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4 text-gray-400" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                              <path d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View for Members */}
              <div className="hidden md:block max-h-96 overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                        {t('name') || 'Name'}
                      </th>
                      <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                        {t('phone') || 'Phone'}
                      </th>
                      <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                        {t('status') || 'Status'}
                      </th>
                      <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                        {t('healthDeclaration') || 'Health Declaration'}
                      </th>
                      <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                        {t('actions') || 'Actions'}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {groupMembers.map((member) => (
                      <tr key={member.id} className="hover:bg-gray-50">
                        <td className={`px-4 py-3 text-sm text-gray-900 ${language === 'he' ? 'text-right' : 'text-left'}`}>
                          {member.field_data?.full_name || '-'}
                        </td>
                        <td className={`px-4 py-3 text-sm text-gray-600 ${language === 'he' ? 'text-right' : 'text-left'}`}>
                          {member.field_data?.phone || '-'}
                        </td>
                        <td className={`px-4 py-3 text-sm ${language === 'he' ? 'text-right' : 'text-left'}`}>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            member.status_name === 'Active' || member.status_name === 'פעיל'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {member.status_name || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          <div className="flex items-center justify-center">
                            <button
                              onClick={() => handleToggleHealthDeclaration(member.id, member.health_declaration)}
                              className={`flex items-center justify-center w-8 h-8 rounded-full transition-all cursor-pointer hover:scale-110 ${
                                member.health_declaration
                                  ? 'bg-green-100 border-2 border-green-500 hover:bg-green-200'
                                  : 'bg-gray-100 border-2 border-gray-300 hover:bg-gray-200'
                              }`}
                              title={member.health_declaration ? (t('healthDeclarationReceived') || 'Health declaration received - Click to cancel') : (t('healthDeclarationNotReceived') || 'No health declaration - Click to mark')}
                            >
                              {member.health_declaration ? (
                                <svg className="w-5 h-5 text-green-600" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                                  <path d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <svg className="w-5 h-5 text-gray-400" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                                  <path d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              )}
                            </button>
                          </div>
                        </td>
                        <td className={`px-4 py-3 text-sm ${language === 'he' ? 'text-right' : 'text-left'}`}>
                          <button
                            onClick={() => handleRemoveMember(member.id)}
                            className="text-red-600 hover:text-red-900"
                            title={t('remove') || 'Remove'}
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div className="flex justify-end pt-4 border-t">
            <Button variant="secondary" onClick={handleCloseMembersModal}>
              {t('close') || 'Close'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Add Member Modal */}
      <Modal
        isOpen={addMemberModalOpen}
        onClose={handleCloseAddMemberModal}
        title={`${t('addMembers') || 'Add Members'} - ${selectedGroup?.group_name || ''}`}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('searchLeads') || 'Search Leads'}
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={t('searchByNamePhoneEmail') || 'Search by name, phone, or email...'}
            />
          </div>

          {loadingLeads ? (
            <div className="text-center py-8">{t('loading')}</div>
          ) : filteredLeads.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {searchTerm
                ? (t('noLeadsFound') || 'No leads found matching your search')
                : (t('noAvailableLeads') || 'No available leads without a group')}
            </div>
          ) : (
            <>
              {/* Mobile Card View for Add Members */}
              <div className="md:hidden max-h-96 overflow-y-auto space-y-3">
                {filteredLeads.map((lead) => (
                  <div key={lead.id} className="bg-gray-50 rounded-lg p-3 space-y-2">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{lead.field_data?.full_name || '-'}</p>
                        <p className="text-sm text-gray-600">{lead.field_data?.phone || '-'}</p>
                      </div>
                      <button
                        onClick={() => handleAddMember(lead.id)}
                        className="px-3 py-1 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                      >
                        {t('add') || 'Add'}
                      </button>
                    </div>
                    <div className="pt-2 border-t border-gray-200">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        lead.status_name === 'Active' || lead.status_name === 'פעיל'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {lead.status_name || '-'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View for Add Members */}
              <div className="hidden md:block max-h-96 overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                        {t('name') || 'Name'}
                      </th>
                      <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                        {t('phone') || 'Phone'}
                      </th>
                      <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                        {t('status') || 'Status'}
                      </th>
                      <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                        {t('actions') || 'Actions'}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredLeads.map((lead) => (
                      <tr key={lead.id} className="hover:bg-gray-50">
                        <td className={`px-4 py-3 text-sm text-gray-900 ${language === 'he' ? 'text-right' : 'text-left'}`}>
                          {lead.field_data?.full_name || '-'}
                        </td>
                        <td className={`px-4 py-3 text-sm text-gray-600 ${language === 'he' ? 'text-right' : 'text-left'}`}>
                          {lead.field_data?.phone || '-'}
                        </td>
                        <td className={`px-4 py-3 text-sm ${language === 'he' ? 'text-right' : 'text-left'}`}>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            lead.status_name === 'Active' || lead.status_name === 'פעיל'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {lead.status_name || '-'}
                          </span>
                        </td>
                        <td className={`px-4 py-3 text-sm ${language === 'he' ? 'text-right' : 'text-left'}`}>
                          <button
                            onClick={() => handleAddMember(lead.id)}
                            className="text-green-600 hover:text-green-900 font-medium"
                          >
                            {t('add') || 'Add'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div className="flex justify-end pt-4 border-t">
            <Button variant="secondary" onClick={handleCloseAddMemberModal}>
              {t('close') || 'Close'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
